import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  TrashIcon,
  Squares2X2Icon,
  SwatchIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { useWorksheets } from '../hooks/useWorksheets';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { CustomFieldType, WorksheetCustomField, WorksheetCustomStatus } from '../types/worksheet';
import CommonPageHeader from '../components/common/CommonPageHeader';
import LoadingSpinner from '../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

const WorksheetSettings: React.FC = () => {
  const {
    customFields,
    customStatuses,
    loadCustomFields,
    loadCustomStatuses,
    saveCustomField,
    deleteCustomField,
    saveCustomStatus,
    deleteCustomStatus,
  } = useWorksheets();
  const { user } = useAuth();

  const [showFieldForm, setShowFieldForm] = useState(false);
  const [showStatusForm, setShowStatusForm] = useState(false);
  const [fieldForm, setFieldForm] = useState({
    name: '',
    type: 'text' as CustomFieldType,
    required: false,
    showInList: false,
    showInPdf: false,
  });
  const [statusForm, setStatusForm] = useState({
    name: '',
    color: '#3b82f6',
    countsAsCompleted: false,
    triggerStockDeduction: false,
    lockEditing: false,
    notifyCustomer: false,
    order: 0,
  });

  useEffect(() => {
    loadCustomFields();
    loadCustomStatuses();
  }, [loadCustomFields, loadCustomStatuses]);

  const handleSaveField = async () => {
    if (!fieldForm.name.trim() || !user?.organizationId) return;
    await saveCustomField({
      ...fieldForm,
      organizationId: user.organizationId,
    });
    setFieldForm({ name: '', type: 'text', required: false, showInList: false, showInPdf: false });
    setShowFieldForm(false);
  };

  const handleSaveStatus = async () => {
    if (!statusForm.name.trim() || !user?.organizationId) return;
    await saveCustomStatus({
      ...statusForm,
      organizationId: user.organizationId,
    });
    setStatusForm({
      name: '',
      color: '#3b82f6',
      countsAsCompleted: false,
      triggerStockDeduction: false,
      lockEditing: false,
      notifyCustomer: false,
      order: customStatuses.length,
    });
    setShowStatusForm(false);
  };

  const [fieldToDelete, setFieldToDelete] = useState<string | null>(null);
  const [statusToDelete, setStatusToDelete] = useState<string | null>(null);

  const handleDeleteField = async (id: string) => {
    setFieldToDelete(id);
  };

  const handleDeleteStatus = async (id: string) => {
    setStatusToDelete(id);
  };

  const confirmDeleteField = async () => {
    if (fieldToDelete) {
      await deleteCustomField(fieldToDelete);
      setFieldToDelete(null);
    }
  };

  const confirmDeleteStatus = async () => {
    if (statusToDelete) {
      await deleteCustomStatus(statusToDelete);
      setStatusToDelete(null);
    }
  };

  return (
    <div className="space-y-4 w-full min-w-0">
      <CommonPageHeader
        title="Munkalap beállítások"
        subtitle="Egyedi mezők és saját státuszok kezelése"
        icon={Cog6ToothIcon}
      />

      <div className="space-y-6">
        {/* Custom Fields */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Squares2X2Icon className="h-5 w-5 text-gray-400 mr-2" />
              <h2 className="text-sm font-medium text-gray-900 dark:text-gray-100">Egyedi mezők</h2>
            </div>
            <button
              onClick={() => setShowFieldForm(!showFieldForm)}
              className="text-sm px-3 py-1.5 rounded-md bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/30 flex items-center gap-1"
            >
              <PlusIcon className="h-4 w-4" />
              Új mező
            </button>
          </div>

          {showFieldForm && (
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Megnevezés</label>
                  <input
                    type="text"
                    value={fieldForm.name}
                    onChange={(e) => setFieldForm({ ...fieldForm, name: e.target.value })}
                    className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Típus</label>
                  <select
                    value={fieldForm.type}
                    onChange={(e) => setFieldForm({ ...fieldForm, type: e.target.value as CustomFieldType })}
                    className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="text">Szöveg</option>
                    <option value="number">Szám</option>
                    <option value="date">Dátum</option>
                    <option value="datetime">Dátum + idő</option>
                  </select>
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={fieldForm.required} onChange={(e) => setFieldForm({ ...fieldForm, required: e.target.checked })} className="mr-2 rounded border-gray-300 text-primary-600" />
                  Kötelező
                </label>
                <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={fieldForm.showInList} onChange={(e) => setFieldForm({ ...fieldForm, showInList: e.target.checked })} className="mr-2 rounded border-gray-300 text-primary-600" />
                  Listában mutat
                </label>
                <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={fieldForm.showInPdf} onChange={(e) => setFieldForm({ ...fieldForm, showInPdf: e.target.checked })} className="mr-2 rounded border-gray-300 text-primary-600" />
                  PDF-ben mutat
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowFieldForm(false)} className="px-3 py-1.5 text-sm rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">Mégse</button>
                <button onClick={handleSaveField} className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700">Mentés</button>
              </div>
            </div>
          )}

          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {customFields.length === 0 ? (
              <div className="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">Még nincsenek egyedi mezők</div>
            ) : (
              customFields.map((field: WorksheetCustomField) => (
                <div key={field.id} className="flex items-center justify-between p-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{field.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {field.type} {field.required && '· kötelező'} {field.showInList && '· listában'} {field.showInPdf && '· PDF-ben'}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteField(field.id)}
                    className="text-gray-400 hover:text-red-600"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Custom Statuses */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <SwatchIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h2 className="text-sm font-medium text-gray-900 dark:text-gray-100">Saját státuszok</h2>
            </div>
            <button
              onClick={() => setShowStatusForm(!showStatusForm)}
              className="text-sm px-3 py-1.5 rounded-md bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/30 flex items-center gap-1"
            >
              <PlusIcon className="h-4 w-4" />
              Új státusz
            </button>
          </div>

          {showStatusForm && (
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Megnevezés</label>
                  <input
                    type="text"
                    value={statusForm.name}
                    onChange={(e) => setStatusForm({ ...statusForm, name: e.target.value })}
                    className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Szín</label>
                  <input
                    type="color"
                    value={statusForm.color}
                    onChange={(e) => setStatusForm({ ...statusForm, color: e.target.value })}
                    className="w-full h-10 rounded-md border border-gray-300 dark:border-gray-600"
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={statusForm.countsAsCompleted} onChange={(e) => setStatusForm({ ...statusForm, countsAsCompleted: e.target.checked })} className="mr-2 rounded border-gray-300 text-primary-600" />
                  Befejezettnek számít
                </label>
                <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={statusForm.triggerStockDeduction} onChange={(e) => setStatusForm({ ...statusForm, triggerStockDeduction: e.target.checked })} className="mr-2 rounded border-gray-300 text-primary-600" />
                  Készletlevonást indít
                </label>
                <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={statusForm.lockEditing} onChange={(e) => setStatusForm({ ...statusForm, lockEditing: e.target.checked })} className="mr-2 rounded border-gray-300 text-primary-600" />
                  Szerkesztés zárolása
                </label>
                <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={statusForm.notifyCustomer} onChange={(e) => setStatusForm({ ...statusForm, notifyCustomer: e.target.checked })} className="mr-2 rounded border-gray-300 text-primary-600" />
                  Ügyfél értesítése
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowStatusForm(false)} className="px-3 py-1.5 text-sm rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">Mégse</button>
                <button onClick={handleSaveStatus} className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700">Mentés</button>
              </div>
            </div>
          )}

          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {customStatuses.length === 0 ? (
              <div className="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">Még nincsenek saját státuszok</div>
            ) : (
              customStatuses.map((status: WorksheetCustomStatus) => (
                <div key={status.id} className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <span className="w-4 h-4 rounded-full" style={{ backgroundColor: status.color }} />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{status.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {status.countsAsCompleted && 'befejezett'} {status.triggerStockDeduction && '· készletlevonás'} {status.lockEditing && '· zárolt'} {status.notifyCustomer && '· értesítés'}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteStatus(status.id)}
                    className="text-gray-400 hover:text-red-600"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Delete field confirmation */}
      {fieldToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/60">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Törlés megerősítése</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Biztosan törlöd ezt az egyedi mezőt?</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setFieldToDelete(null)} className="px-4 py-2 text-sm rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">Mégse</button>
              <button onClick={confirmDeleteField} className="px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700">Törlés</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete status confirmation */}
      {statusToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/60">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Törlés megerősítése</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Biztosan törlöd ezt a saját státuszt?</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setStatusToDelete(null)} className="px-4 py-2 text-sm rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">Mégse</button>
              <button onClick={confirmDeleteStatus} className="px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700">Törlés</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorksheetSettings;
